/**
 * useStorageSignal — Re-acciona cuando OTRA pestaña cambia una clave local.
 *
 * QUÉ HACE: Escucha el evento `storage` (cambios de localStorage hechos por
 *   otra pestaña/ventana del mismo origen) y llama a `onSignal` cuando cambia
 *   alguna de las claves indicadas (prefijos válidos) o cuando se limpia todo
 *   el storage. Sirve para adelantar el polling de los watchers: el aviso de
 *   pedido nuevo aparece al instante en demo/local, sin esperar el intervalo.
 * QUÉ EXPORTA: useStorageSignal.
 * QUÉ NO DEBE HACER: No lee datos ni conoce reglas de negocio; no dispara en
 *   la MISMA pestaña que hizo el cambio (limitación del evento `storage`).
 */
import { useEffect, useRef } from "react";

export function useStorageSignal(
  keys: string | readonly string[],
  onSignal: () => void,
): void {
  const onSignalRef = useRef(onSignal);
  useEffect(() => {
    onSignalRef.current = onSignal;
  }, [onSignal]);

  // Ref con las claves actuales: la suscripción se monta una sola vez.
  const keysRef = useRef<string[]>(
    typeof keys === "string" ? [keys] : [...keys],
  );
  useEffect(() => {
    keysRef.current = typeof keys === "string" ? [keys] : [...keys];
  }, [keys]);

  useEffect(() => {
    const handler = (event: StorageEvent): void => {
      const changed = event.key;
      if (
        changed === null || // localStorage.clear()
        keysRef.current.some(
          (key) => changed === key || changed.startsWith(`${key}_`),
        )
      ) {
        onSignalRef.current();
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);
}
